import i18n from 'i18next'
import { useTranslation } from 'react-i18next'

import { PerModal } from '../Modal'
import { useAppraisalStatusModal } from '../../../graphql/meeting/hooks/useAppraisalStatusModal'
import { FullMeetingPreviewDto, Meeting, MeetingType, Country } from '../../../types'
import { getEmployeeFullName, getReviewerFullName } from '../../../shared/employeeUtil'
import { MeetingStatusModalContainer } from './MeetingStatusModalContainer'
import { CurrencyFromContractProvider } from '../../../context/currency-context'
import { useAuth } from '../../../context/auth-context'
import { isUserHrForCountry } from '../../../shared/rolesUtil'
import { NewLoadingAndErrors } from '../LoadingAndErrors/NewLoadingAndErrors'
import PerFlexRow from '../FlexRow'

export interface MeetingStatusModalData {
  meetingId: string
  employeeId: string
  meetingType: MeetingType
  country: Country
}
export interface MeetingStatusModalProps extends MeetingStatusModalData {
  onClose: () => void
}

const generateSubHeading = (meeting: Meeting) => {
  return i18n.t('appraisalStatusModal.modalSubheading', {
    reviewer: getReviewerFullName(meeting.reviewer),
    employee: getEmployeeFullName(meeting.employee),
  })
}

export const MeetingStatusModal = ({ meetingId, employeeId, meetingType, onClose, country }: MeetingStatusModalProps): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isHrForCountry = isUserHrForCountry(country, user.roles)

  const isAppraisalUpdate = meetingType === MeetingType.AppraisalUpdate

  const { data, loading, error } = useAppraisalStatusModal(meetingId, employeeId, country, isAppraisalUpdate)

  const fullMeetingPreview = !!data ? (data.fullMeetingPreview as FullMeetingPreviewDto) : undefined

  return (
    <PerModal
      title={{
        heading: !!isAppraisalUpdate ? t('appraisalStatusModal.updateTitle') : t('appraisalStatusModal.title'),
        subheading: !!fullMeetingPreview ? generateSubHeading(fullMeetingPreview.meeting) : undefined,
      }}
      size="xlarge"
      centered
      visible={!!meetingId && !!employeeId}
      onCancel={onClose}
    >
      {loading || error || !data || !data.fullMeetingPreview ? (
        <PerFlexRow my="xs" width="fullWidth" justify="center">
          <NewLoadingAndErrors isLoading={loading} error={error} hasNoData={!data || !data.fullMeetingPreview} showSpinner t={t} />
        </PerFlexRow>
      ) : (
        <div data-testid="MeetingStatusModalContainer">
          <CurrencyFromContractProvider employeeId={employeeId}>
            <MeetingStatusModalContainer
              fullMeetingPreview={data.fullMeetingPreview as FullMeetingPreviewDto}
              employeeTimezone={data.branchOfficeByEmployee.timezone}
              isAppraisalUpdate={isAppraisalUpdate}
              isBonusValidationEnabled={data.isBonusValidationEnabled}
              userIsHR={isHrForCountry}
            />
          </CurrencyFromContractProvider>
        </div>
      )}
    </PerModal>
  )
}
